/**
 * Per-route document metadata: the page title and whether search engines may
 * index the page. One table drives `<PageMeta>`, `public/robots.txt` (a test
 * keeps them in step) and the tests.
 *
 * Indexable: the landing page, the help centre and the legal pages. Every
 * other route (the signed-in app, the auth screens, the decoy notes page and
 * unknown URLs) is `noindex, nofollow`.
 */
import { BRAND_NAME } from '../brand/tokens';
import type { LocalizedText } from '../i18n/localizedText';

export const DEFAULT_APP_TITLE: string = BRAND_NAME.en;
export const ROBOTS_INDEX = 'index, follow';
export const ROBOTS_NOINDEX = 'noindex, nofollow';

export interface RouteMeta {
  /** `null` = the title is the app name alone (landing page). */
  title: LocalizedText | null;
  indexable: boolean;
}

interface RouteRule extends RouteMeta {
  path: string;
  /** `exact` matches that path only; `prefix` the path and everything below it. */
  match: 'exact' | 'prefix';
}

const t = (en: string, ar: string): LocalizedText => ({ en, ar });

/** First match wins, so list longer paths before their parents. */
const ROUTES: RouteRule[] = [
  { path: '/', match: 'exact', indexable: true, title: null },

  {
    path: '/help/playbook',
    match: 'prefix',
    indexable: true,
    title: t('Playbook', 'دليل اللعب'),
  },
  {
    path: '/help/catalog',
    match: 'prefix',
    indexable: true,
    title: t("What's inside", 'ما بداخل التطبيق'),
  },
  {
    path: '/help/faq',
    match: 'exact',
    indexable: true,
    title: t('FAQ', 'الأسئلة الشائعة'),
  },
  {
    path: '/help/support',
    match: 'exact',
    indexable: true,
    title: t('Support', 'الدعم'),
  },
  {
    path: '/help',
    match: 'prefix',
    indexable: true,
    title: t('Help centre', 'مركز المساعدة'),
  },

  {
    path: '/legal/terms',
    match: 'exact',
    indexable: true,
    title: t('Terms of use', 'شروط الاستخدام'),
  },
  {
    path: '/legal/privacy',
    match: 'exact',
    indexable: true,
    title: t('Privacy policy', 'سياسة الخصوصية'),
  },
  {
    path: '/legal/cookies',
    match: 'exact',
    indexable: true,
    title: t(
      'Cookies and local storage',
      'ملفات تعريف الارتباط والتخزين المحلي'
    ),
  },
  {
    path: '/legal/age',
    match: 'exact',
    indexable: true,
    title: t('18+ only', 'لمن هم فوق 18 عامًا فقط'),
  },
  {
    path: '/legal',
    match: 'prefix',
    indexable: true,
    title: t('Legal', 'المعلومات القانونية'),
  },

  {
    path: '/auth/login',
    match: 'exact',
    indexable: false,
    title: t('Sign in', 'تسجيل الدخول'),
  },
  {
    path: '/auth/register',
    match: 'exact',
    indexable: false,
    title: t('Create account', 'إنشاء حساب'),
  },
  {
    path: '/start',
    match: 'exact',
    indexable: false,
    title: t('Play now', 'العب دلوقتي'),
  },
  {
    path: '/auth/forgot-password',
    match: 'exact',
    indexable: false,
    title: t('Forgot password', 'نسيت كلمة المرور'),
  },
  {
    path: '/reset-password',
    match: 'exact',
    indexable: false,
    title: t('Choose a new password', 'اختيار كلمة مرور جديدة'),
  },
  {
    path: '/login',
    match: 'exact',
    indexable: false,
    title: t('Sign in', 'تسجيل الدخول'),
  },
  {
    path: '/pair',
    match: 'exact',
    indexable: false,
    title: t('Pair with your partner', 'الربط مع شريكك'),
  },
  {
    path: '/onboarding',
    match: 'exact',
    indexable: false,
    title: t('Welcome', 'مرحبًا'),
  },
  {
    path: '/dashboard',
    match: 'exact',
    indexable: false,
    title: t('Home', 'الرئيسية'),
  },
  {
    path: '/partner',
    match: 'exact',
    indexable: false,
    title: t('Partner', 'الشريك'),
  },
  {
    path: '/rewards',
    match: 'exact',
    indexable: false,
    title: t('Rewards', 'المكافآت'),
  },
  {
    path: '/game',
    match: 'prefix',
    indexable: false,
    title: t('Game', 'اللعبة'),
  },
  {
    path: '/settings',
    match: 'exact',
    indexable: false,
    title: t('Settings', 'الإعدادات'),
  },
  {
    path: '/history',
    match: 'exact',
    indexable: false,
    title: t('History', 'السجل'),
  },
  {
    path: '/fantasy-catalog',
    match: 'exact',
    indexable: false,
    title: t('Fantasies', 'الخيالات'),
  },
  {
    path: '/notes',
    match: 'exact',
    indexable: false,
    title: t('Notes', 'ملاحظات'),
  },
];

export const NOT_FOUND_META: RouteMeta = {
  indexable: false,
  title: t('Page not found', 'الصفحة غير موجودة'),
};

function normalise(pathname: string): string {
  const withoutQuery = pathname.split(/[?#]/)[0] || '/';
  return withoutQuery.length > 1 ? withoutQuery.replace(/\/+$/, '') : '/';
}

function matches(rule: RouteRule, pathname: string): boolean {
  if (rule.match === 'exact') return pathname === rule.path;
  return pathname === rule.path || pathname.startsWith(`${rule.path}/`);
}

/** Metadata for a URL path; unknown paths are the not-found page. */
export function resolveRouteMeta(pathname: string): RouteMeta {
  const path = normalise(pathname);
  const rule = ROUTES.find(candidate => matches(candidate, path));
  return rule
    ? { title: rule.title, indexable: rule.indexable }
    : NOT_FOUND_META;
}

/** Top-level path prefixes of the non-indexable app routes (for robots.txt). */
export const NOINDEX_ROUTE_PREFIXES: string[] = [
  ...new Set(
    ROUTES.filter(rule => !rule.indexable).map(
      rule => `/${rule.path.split('/')[1]}`
    )
  ),
];

/** Top-level prefixes of the indexable routes (the `/` landing page aside). */
export const INDEXABLE_ROUTE_PREFIXES: string[] = [
  ...new Set(
    ROUTES.filter(rule => rule.indexable && rule.path !== '/').map(
      rule => `/${rule.path.split('/')[1]}`
    )
  ),
];

/**
 * `<title>` text. A deployment with its own `VITE_APP_TITLE` (for example the
 * disguised "Notes" build) keeps that title on every page.
 */
export function formatDocumentTitle(
  pageTitle: string | null,
  appTitle: string = DEFAULT_APP_TITLE,
  language: keyof typeof BRAND_NAME = 'en'
): string {
  const app = appTitle.trim() || DEFAULT_APP_TITLE;
  if (app !== DEFAULT_APP_TITLE) return app;
  const brand = BRAND_NAME[language] ?? DEFAULT_APP_TITLE;
  return pageTitle ? `${pageTitle} · ${brand}` : brand;
}
